import { useMutation, useQuery, useQueryClient } from 'react-query';
import { CAPTURE_AI_SCAN_TYPES_FACILITY_OPTION_NAME } from 'common/lib/model/facility/FacilityOptions';
import * as documentScanTypesApi from 'src/api/documentScanTypes';
import * as facilitiesApi from 'src/api/facilities';

export type DocumentScanSupportedEntityType = 'encounter' | 'encounter_form';
export type DocumentScanCaptureMode = 'unnamed_pages' | 'named_slots';
export type DocumentScanTypeVersionState = 'draft' | 'published';
export type FacilityAvailabilityMode = 'all_facilities' | 'selected_facilities';
export type FacilityOverrideStatus = 'inherits' | 'draft' | 'published' | 'removal_draft';

export interface DocumentScanTypeTemplate {
    templateKey: string;
    scanTypeKey: string;
    displayName: string;
    description: string | null;
    supportedEntityType: DocumentScanSupportedEntityType;
    captureMode: DocumentScanCaptureMode;
    captureConfig: Record<string, unknown>;
    allowEntityCreation: boolean;
    updateLinkedEntity: boolean;
}

export interface DocumentScanTypeVersionSummary {
    documentScanTypeVersionId: string;
    versionNumber: number;
    versionState: DocumentScanTypeVersionState;
    displayName: string;
    description: string | null;
    promptText: string;
    extractionSchema: Record<string, unknown> | null;
    entityMappingSchema: Record<string, unknown> | null;
    captureMode: DocumentScanCaptureMode;
    captureConfig: Record<string, unknown>;
    allowEntityCreation: boolean;
    entityCreationConfig: Record<string, unknown> | null;
    updateLinkedEntity: boolean;
    requiresReviewBeforeEntityUpdate: boolean;
    createdAt?: string | Date;
    publishedAt: string | Date | null;
    createdByUserId: number | null;
    publishedByUserId: number | null;
}

export interface DocumentScanTypeAdminSummary {
    documentScanTypeId: string;
    scanTypeKey: string;
    baseScanTypeKey: string | null;
    displayName: string;
    description: string | null;
    supportedEntityType: DocumentScanSupportedEntityType;
    publishedVersion: DocumentScanTypeVersionSummary | null;
    draftVersion: DocumentScanTypeVersionSummary | null;
    versions?: DocumentScanTypeVersionSummary[];
    hasDraft: boolean;
}

export interface CreateDocumentScanTypeFromTemplateInput {
    templateKey: string;
    scanTypeKey?: string;
    name?: string;
    description?: string | null;
    supportedEntityType?: DocumentScanSupportedEntityType;
}

export interface CreateDocumentScanTypeInput {
    scanTypeKey: string;
    name: string;
    description?: string | null;
    supportedEntityType: DocumentScanSupportedEntityType;
    promptText: string;
    extractionSchema?: Record<string, unknown> | null;
    entityMappingSchema?: Record<string, unknown> | null;
    captureMode: DocumentScanCaptureMode;
    captureConfig: Record<string, unknown>;
    allowEntityCreation: boolean;
    entityCreationConfig?: Record<string, unknown> | null;
    updateLinkedEntity: boolean;
    requiresReviewBeforeEntityUpdate: boolean;
}

export interface UpdateDocumentScanTypeSettingsInput {
    documentScanTypeId: string;
    data: {
        name?: string;
        description?: string | null;
    };
}

export interface DocumentScanTypeValidationResult {
    valid: boolean;
    errors: string[];
}

export interface DocumentScanTypeTestResult {
    status: string;
    steps: Array<{ step: string; status: string }>;
    validationErrors: string[];
}

export interface FacilityDocumentScanTypeVersionSummary {
    facilityDocumentScanTypeVersionId: string;
    versionNumber: number;
    versionState: DocumentScanTypeVersionState;
    overrides: Record<string, unknown>;
    hasOverride: boolean;
    createdAt?: string | Date;
    publishedAt: string | Date | null;
    createdByUserId: number | null;
    publishedByUserId: number | null;
}

export interface FacilityDocumentScanTypeAvailabilitySummary {
    facilityDocumentScanTypeId: string | null;
    documentScanTypeId: string;
    facilityId: number;
    facilityName: string | null;
    captureAiScanTypesEnabled: boolean;
    availabilityEnabled: boolean;
    overrideStatus: FacilityOverrideStatus;
    overrideFieldCount: number;
    publishedVersion: FacilityDocumentScanTypeVersionSummary | null;
    draftVersion: FacilityDocumentScanTypeVersionSummary | null;
    versions?: FacilityDocumentScanTypeVersionSummary[];
    hasDraft: boolean;
}

export interface DocumentScanTypeFacilityAvailabilitySummary {
    mode: FacilityAvailabilityMode;
    facilityIds: number[];
    facilities: FacilityDocumentScanTypeAvailabilitySummary[];
}

export interface DocumentScanTypeFormDefinitionAssociation {
    documentScanTypeFormDefinitionId: string;
    documentScanTypeId: string;
    facilityId: number;
    facilityName: string | null;
    facilityNameInternal: string | null;
    formDefinitionId: number;
    formDefinitionName: string | null;
    formDefinitionTitle: string | null;
    formDefinitionVersion: number | null;
    active: boolean;
    facilityAvailabilityEnabled: boolean;
    effectiveActive: boolean;
    createdAt?: string | Date;
    updatedAt?: string | Date;
}

export interface FormDefinitionDocumentScanTypeAssociation extends DocumentScanTypeFormDefinitionAssociation {
    scanTypeKey: string;
    displayName: string;
    description: string | null;
    allowEntityCreation: boolean;
    updateLinkedEntity: boolean;
}

export const documentScanTypeTemplatesKey = ['documentScanTypeTemplates'] as const;
export const adminDocumentScanTypesKey = ['adminDocumentScanTypes'] as const;
export const adminDocumentScanTypeKey = (documentScanTypeId: string) =>
    ['adminDocumentScanType', documentScanTypeId] as const;
export const documentScanTypeFacilityAvailabilityKey = (documentScanTypeId: string) =>
    ['documentScanTypeFacilityAvailability', documentScanTypeId] as const;
export const documentScanTypeFormDefinitionsKey = (documentScanTypeId: string) =>
    ['documentScanTypeFormDefinitions', documentScanTypeId] as const;
export const formDefinitionDocumentScanTypesKey = (formDefinitionId: number) =>
    ['formDefinitionDocumentScanTypes', formDefinitionId] as const;

export const useDocumentScanTypeTemplates = () => {
    return useQuery({
        queryKey: documentScanTypeTemplatesKey,
        queryFn: documentScanTypesApi.getDocumentScanTypeTemplates,
    });
};

export const useAdminDocumentScanTypes = () => {
    return useQuery({
        queryKey: adminDocumentScanTypesKey,
        queryFn: documentScanTypesApi.getAdminDocumentScanTypes,
    });
};

export const useAdminDocumentScanType = (documentScanTypeId: string) => {
    return useQuery({
        queryKey: adminDocumentScanTypeKey(documentScanTypeId),
        queryFn: () => documentScanTypesApi.getAdminDocumentScanType(documentScanTypeId),
        enabled: !!documentScanTypeId,
    });
};

export const useCreateDocumentScanTypeFromTemplate = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CreateDocumentScanTypeFromTemplateInput) =>
            documentScanTypesApi.createDocumentScanTypeFromTemplate(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: adminDocumentScanTypesKey });
        },
    });
};

export const useCreateDocumentScanType = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CreateDocumentScanTypeInput) => documentScanTypesApi.createDocumentScanType(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: adminDocumentScanTypesKey });
        },
    });
};

export const useCreateDocumentScanTypeDraft = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (documentScanTypeId: string) => documentScanTypesApi.createDocumentScanTypeDraft(documentScanTypeId),
        onSuccess: (_, documentScanTypeId) => {
            queryClient.invalidateQueries({ queryKey: adminDocumentScanTypesKey });
            queryClient.invalidateQueries({ queryKey: adminDocumentScanTypeKey(documentScanTypeId) });
        },
    });
};

export const useUpdateDocumentScanTypeDraft = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: documentScanTypesApi.updateDocumentScanTypeDraft,
        onSuccess: (_, { documentScanTypeId }) => {
            queryClient.invalidateQueries({ queryKey: adminDocumentScanTypesKey });
            queryClient.invalidateQueries({ queryKey: adminDocumentScanTypeKey(documentScanTypeId) });
        },
    });
};

export const useUpdateDocumentScanTypeSettings = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: documentScanTypesApi.updateDocumentScanTypeSettings,
        onSuccess: (_, { documentScanTypeId }) => {
            queryClient.invalidateQueries({ queryKey: adminDocumentScanTypesKey });
            queryClient.invalidateQueries({ queryKey: adminDocumentScanTypeKey(documentScanTypeId) });
        },
    });
};

export const useValidateDocumentScanTypeDraft = () => {
    return useMutation({
        mutationFn: documentScanTypesApi.validateDocumentScanTypeDraft,
    });
};

export const useTestDocumentScanTypeDraft = () => {
    return useMutation({
        mutationFn: documentScanTypesApi.testDocumentScanTypeDraft,
    });
};

export const useTestDocumentScanTypeVersion = () => {
    return useMutation({
        mutationFn: documentScanTypesApi.testDocumentScanTypeVersion,
    });
};

export const usePublishDocumentScanTypeDraft = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: documentScanTypesApi.publishDocumentScanTypeDraft,
        onSuccess: (_, { documentScanTypeId }) => {
            queryClient.invalidateQueries({ queryKey: adminDocumentScanTypesKey });
            queryClient.invalidateQueries({ queryKey: adminDocumentScanTypeKey(documentScanTypeId) });
        },
    });
};

export const usePublishDocumentScanTypeVersion = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: documentScanTypesApi.publishDocumentScanTypeVersion,
        onSuccess: (_, { documentScanTypeId }) => {
            queryClient.invalidateQueries({ queryKey: adminDocumentScanTypesKey });
            queryClient.invalidateQueries({ queryKey: adminDocumentScanTypeKey(documentScanTypeId) });
        },
    });
};

export const useDocumentScanTypeFacilityAvailability = (documentScanTypeId: string) => {
    return useQuery({
        queryKey: documentScanTypeFacilityAvailabilityKey(documentScanTypeId),
        queryFn: () => documentScanTypesApi.getDocumentScanTypeFacilityAvailability(documentScanTypeId),
        enabled: !!documentScanTypeId,
    });
};

export const useUpdateDocumentScanTypeFacilityAvailability = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: documentScanTypesApi.updateDocumentScanTypeFacilityAvailability,
        onSuccess: (_, { documentScanTypeId }) => {
            queryClient.invalidateQueries({ queryKey: documentScanTypeFacilityAvailabilityKey(documentScanTypeId) });
            queryClient.invalidateQueries({ queryKey: documentScanTypeFormDefinitionsKey(documentScanTypeId) });
        },
    });
};

export const useEnableFacilityDocumentScanTypes = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            facilityId,
            disableLegacyCaptureAi,
        }: {
            documentScanTypeId: string;
            facilityId: number;
            disableLegacyCaptureAi?: boolean;
        }) =>
            facilitiesApi.updateFacility(facilityId, {
                facId: facilityId,
                facilityOptions: {
                    [CAPTURE_AI_SCAN_TYPES_FACILITY_OPTION_NAME]: true,
                    ...(disableLegacyCaptureAi ? { encounterScanEnabled: false } : {}),
                },
            }),
        onSuccess: (_, { documentScanTypeId }) => {
            queryClient.invalidateQueries({ queryKey: documentScanTypeFacilityAvailabilityKey(documentScanTypeId) });
            queryClient.invalidateQueries({ queryKey: documentScanTypeFormDefinitionsKey(documentScanTypeId) });
        },
    });
};

export const useDocumentScanTypeFormDefinitions = (documentScanTypeId: string, enabled = true) => {
    return useQuery({
        queryKey: documentScanTypeFormDefinitionsKey(documentScanTypeId),
        queryFn: () => documentScanTypesApi.getDocumentScanTypeFormDefinitions(documentScanTypeId),
        enabled: enabled && !!documentScanTypeId,
    });
};

export const useAddDocumentScanTypeFormDefinition = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: documentScanTypesApi.addDocumentScanTypeFormDefinition,
        onSuccess: (_, { documentScanTypeId }) => {
            queryClient.invalidateQueries({ queryKey: documentScanTypeFormDefinitionsKey(documentScanTypeId) });
        },
    });
};

export const useDeactivateDocumentScanTypeFormDefinition = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: documentScanTypesApi.deactivateDocumentScanTypeFormDefinition,
        onSuccess: (_, { documentScanTypeId }) => {
            queryClient.invalidateQueries({ queryKey: documentScanTypeFormDefinitionsKey(documentScanTypeId) });
        },
    });
};

export const useFormDefinitionDocumentScanTypes = (formDefinitionId: number) => {
    return useQuery({
        queryKey: formDefinitionDocumentScanTypesKey(formDefinitionId),
        queryFn: () => documentScanTypesApi.getFormDefinitionDocumentScanTypes(formDefinitionId),
        enabled: !!formDefinitionId,
    });
};

export const useCreateFacilityDocumentScanType = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: documentScanTypesApi.createFacilityDocumentScanType,
        onSuccess: (_, { data }) => {
            queryClient.invalidateQueries({
                queryKey: documentScanTypeFacilityAvailabilityKey(`${data.documentScanTypeId}`),
            });
        },
    });
};

export const useCreateDocumentScanTypeFacilityOverrideDraft = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: documentScanTypesApi.createDocumentScanTypeFacilityOverrideDraft,
        onSuccess: (_, { documentScanTypeId }) => {
            queryClient.invalidateQueries({ queryKey: documentScanTypeFacilityAvailabilityKey(documentScanTypeId) });
        },
    });
};

export const useUpdateDocumentScanTypeFacilityOverrideDraft = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: documentScanTypesApi.updateDocumentScanTypeFacilityOverrideDraft,
        onSuccess: (_, { documentScanTypeId }) => {
            queryClient.invalidateQueries({ queryKey: documentScanTypeFacilityAvailabilityKey(documentScanTypeId) });
        },
    });
};

export const usePublishDocumentScanTypeFacilityOverrideDraft = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: documentScanTypesApi.publishDocumentScanTypeFacilityOverrideDraft,
        onSuccess: (_, { documentScanTypeId }) => {
            queryClient.invalidateQueries({ queryKey: documentScanTypeFacilityAvailabilityKey(documentScanTypeId) });
        },
    });
};

export const useDiscardDocumentScanTypeFacilityOverrideDraft = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: documentScanTypesApi.discardDocumentScanTypeFacilityOverrideDraft,
        onSuccess: (_, { documentScanTypeId }) => {
            queryClient.invalidateQueries({ queryKey: documentScanTypeFacilityAvailabilityKey(documentScanTypeId) });
        },
    });
};

export const useCreateFacilityDocumentScanTypeDraft = () => {
    return useMutation({
        mutationFn: documentScanTypesApi.createFacilityDocumentScanTypeDraft,
    });
};

export const useUpdateFacilityDocumentScanTypeDraft = () => {
    return useMutation({
        mutationFn: documentScanTypesApi.updateFacilityDocumentScanTypeDraft,
    });
};

export const usePublishFacilityDocumentScanTypeDraft = () => {
    return useMutation({
        mutationFn: documentScanTypesApi.publishFacilityDocumentScanTypeDraft,
    });
};
